import { PageContext } from '@websrc/App/AppContext'
import { AppProvider } from '@websrc/App/AppProvider'
import { DataLayer } from '@websrc/App/Components/DataLayer'
import { ProductPageDetails } from '@websrc/App/Components/Details/ProductPageDetails'
import { ExitPopup } from '@websrc/App/Components/ExitPopup/ExitPopup'
import { createTrackingProduct } from '@websrc/App/Helpers/adobeTracking'
import { useRenderExitPopup } from '@websrc/App/Hooks/useRenderExitPopup'
import { fbPixel } from '@websrc/App/Tracking/FacebookPixel'
import { useProductMeta } from '@websrc/Basket/useProductMeta'
import { ExitPopupData } from '@websrc/DataMappers/mapExitPopup'
import { mapProductPage } from '@websrc/DataMappers/mapProductPage'
import { SpotCollapse } from '@websrc/Spot/Collapse/SpotCollapse'
import { styles } from 'leds'
import React, { useEffect } from 'react'

export interface PageRenderProps {
  page: ReturnType<typeof mapProductPage>
  exitPopup?: ExitPopupData
}

export interface ProductPageProps {
  pageContext: PageContext & PageRenderProps
}

export function ProductPageRender({ page, exitPopup }: PageRenderProps) {
  const dataLayer = DataLayer._useRawDataLayer()
  const meta = useProductMeta(page.product_details.product_number)
  const exitPopupDisabled = page.disableExitPopup
  const renderExitPopup = useRenderExitPopup(!exitPopupDisabled) && exitPopup

  useEffect(() => {
    dataLayer.products = [createTrackingProduct(meta, 1, page.product_details.price_discount)]
    fbPixel.trackViewContent(meta, page.product_details.price_discount)
  }, [dataLayer, page, meta])

  return (
    <>
      {renderExitPopup && <ExitPopup {...exitPopup} />}
      <ProductPageDetails
        isTopOfPage={true}
        productDetails={page.product_details}
        warnings={page.warnings}
      />
      <div className={styles({ marginBottom: 50 })}>
        {page.spots?.map((_, i) => (
          <SpotCollapse key={`spot-parent-${i}`} spotIndex={i} spots={page.spots} />
        ))}
      </div>
    </>
  )
}

export default function ProductPage({ pageContext }: ProductPageProps) {
  const { page, appContext } = pageContext
  const siteSection = 'legoeducation:product-details'
  const pageName = `${siteSection}:${page.product_details.product_number}`
  const pageType = 'product-details'

  return (
    <AppProvider
      {...appContext}
      matomoPageTemplate={'product_detail'}
      pageName={pageName}
      pageType={pageType}
      productDetails={page.product_details}
      siteSection={siteSection}
    >
      <ProductPageRender exitPopup={appContext.exitPopup} page={page} />
    </AppProvider>
  )
}
